import React, { useCallback, useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  adminAnalyticsService,
  UsersAnalytics,
  SearchFunnelAnalytics,
  ContentAnalytics,
  AiUsageAnalytics,
  ApiRequestsAnalytics,
  TranscriptionAnalytics
} from '../../services/admin/admin-analytics.service';
import { exportToCsv } from '../../utils/csv-export';
import AdminNavBar from './AdminNavBar';

// ── Palette ────────────────────────────────────────────────────────────────
const COLORS = [
  '#4f46e5',
  '#06b6d4',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6'
];

// ── Day-range selector ────────────────────────────────────────────────────
const RANGES = [7, 14, 30, 60, 90];

function RangeSelector({
  value,
  onChange
}: {
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className='btn-group btn-group-sm aa-range-group'>
      {RANGES.map((d) => (
        <button
          key={d}
          className={`btn ${value === d ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => onChange(d)}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  sub
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className='aa-stat-card'>
      <div className='aa-stat-value'>{value}</div>
      <div className='aa-stat-label'>{label}</div>
      {sub && <div className='aa-stat-sub'>{sub}</div>}
    </div>
  );
}

// ── Loading / error helpers ───────────────────────────────────────────────
function LoadingSpinner() {
  return (
    <div className='aa-center'>
      <div
        className='spinner-border text-primary'
        role='status'
      />
    </div>
  );
}

function ErrorAlert({ message }: { message: string }) {
  return <div className='alert alert-danger m-3'>{message}</div>;
}

// ── Date formatter for chart x-axis ──────────────────────────────────────
function shortDate(iso: string) {
  const [, m, d] = iso.split('-');
  return `${m}/${d}`;
}

// ── Tabs ──────────────────────────────────────────────────────────────────
type TabId = 'users' | 'search' | 'content' | 'ai' | 'api' | 'transcription';

const TABS: { id: TabId; label: string }[] = [
  { id: 'users', label: 'Users' },
  { id: 'search', label: 'Search Funnel' },
  { id: 'content', label: 'Content' },
  { id: 'ai', label: 'AI Usage' },
  { id: 'api', label: 'API Requests' },
  { id: 'transcription', label: 'Transcription' }
];

// ── Transcription Tab ────────────────────────────────────────────────────
function TranscriptionTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<TranscriptionAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await adminAnalyticsService.getTranscription(days));
    } catch {
      setError('Failed to load transcription analytics.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const retryTier3 = async (sourceId: string) => {
    setActionError('');
    try {
      const reason = window.prompt(
        'Optional reason for Tier 3 retry:',
        'operational triage retry'
      );
      await adminAnalyticsService.retryYouTubeTier3(
        sourceId,
        reason ?? undefined
      );
    } catch {
      setActionError('Failed to request Tier 3 retry.');
    }
  };

  return (
    <div>
      <div className='aa-tab-header'>
        <h5 className='aa-tab-title'>Transcription Triage</h5>
        <RangeSelector
          value={days}
          onChange={setDays}
        />
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorAlert message={error} />}
      {actionError && <ErrorAlert message={actionError} />}

      {data && !loading && (
        <>
          <div className='aa-stat-row'>
            <StatCard
              label='Total Failures'
              value={data.totalFailures.toLocaleString()}
              sub={`last ${days} days`}
            />
            <StatCard
              label='Unique Videos'
              value={data.uniqueVideos.toLocaleString()}
              sub='with failures'
            />
          </div>

          <div className='aa-chart-row'>
            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Failures / Day</div>
              <ResponsiveContainer
                width='100%'
                height={220}
              >
                <LineChart data={data.failuresByDay}>
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip labelFormatter={(v: unknown) => String(v)} />
                  <Line
                    type='monotone'
                    dataKey='failures'
                    name='Failures'
                    stroke={COLORS[4]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className='aa-chart-card'>
              <div className='aa-chart-title aa-chart-title--row'>
                Top Failure Reasons
                {data.failureReasons.length > 0 && (
                  <button
                    className='btn btn-xs btn-outline-secondary aa-export-btn'
                    onClick={() =>
                      exportToCsv(
                        'transcription-failure-reasons.csv',
                        data.failureReasons as unknown as Record<
                          string,
                          string | number
                        >[],
                        [
                          { key: 'reason', label: 'Reason' },
                          { key: 'count', label: 'Count' }
                        ]
                      )
                    }
                  >
                    Export CSV
                  </button>
                )}
              </div>
              <table className='table table-sm aa-table'>
                <thead>
                  <tr>
                    <th>Reason</th>
                    <th className='text-end'>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.failureReasons.map((r) => (
                    <tr key={r.reason}>
                      <td className='aa-wrap-cell'>{r.reason}</td>
                      <td className='text-end'>{r.count.toLocaleString()}</td>
                    </tr>
                  ))}
                  {data.failureReasons.length === 0 && (
                    <tr>
                      <td
                        colSpan={2}
                        className='text-center text-muted'
                      >
                        No failure reasons in selected range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className='aa-chart-card aa-chart-card--full'>
            <div className='aa-chart-title aa-chart-title--row'>
              Top Failing Videos
              {data.topFailingVideos.length > 0 && (
                <button
                  className='btn btn-xs btn-outline-secondary aa-export-btn'
                  onClick={() =>
                    exportToCsv(
                      'transcription-top-failing-videos.csv',
                      data.topFailingVideos.map((v) => ({
                        videoId: v.videoId,
                        title: v.title ?? '',
                        failures: v.failures,
                        lastError: v.lastError,
                        lastAttemptAt: v.lastAttemptAt ?? '',
                        attemptedTiers: (v.attemptedTiers ?? []).join('|'),
                        sourceId: v.sourceId,
                        url: v.url ?? ''
                      })),
                      [
                        { key: 'videoId', label: 'Video ID' },
                        { key: 'title', label: 'Title' },
                        { key: 'failures', label: 'Failures' },
                        { key: 'lastError', label: 'Last Error' },
                        { key: 'lastAttemptAt', label: 'Last Attempt' },
                        { key: 'attemptedTiers', label: 'Attempted Tiers' },
                        { key: 'sourceId', label: 'Source ID' },
                        { key: 'url', label: 'URL' }
                      ]
                    )
                  }
                >
                  Export CSV
                </button>
              )}
            </div>
            <table className='table table-sm aa-table'>
              <thead>
                <tr>
                  <th>Video</th>
                  <th className='text-end'>Failures</th>
                  <th>Last Error</th>
                  <th className='text-end'>Last Attempt</th>
                  <th className='text-end'>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.topFailingVideos.map((v) => (
                  <tr key={`${v.sourceId}-${v.videoId}`}>
                    <td>
                      <div className='fw-semibold'>{v.videoId}</div>
                      {v.title && (
                        <div className='text-muted small'>{v.title}</div>
                      )}
                    </td>
                    <td className='text-end'>{v.failures.toLocaleString()}</td>
                    <td className='aa-wrap-cell'>{v.lastError}</td>
                    <td className='text-end'>
                      {v.lastAttemptAt
                        ? new Date(v.lastAttemptAt).toLocaleString()
                        : '—'}
                    </td>
                    <td className='text-end'>
                      <button
                        className='btn btn-sm btn-outline-primary'
                        onClick={() => retryTier3(v.sourceId)}
                      >
                        Retry Tier 3
                      </button>
                    </td>
                  </tr>
                ))}
                {data.topFailingVideos.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className='text-center text-muted'
                    >
                      No failing videos in selected range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────
function UsersTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<UsersAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await adminAnalyticsService.getUsers(days));
    } catch {
      setError('Failed to load user analytics.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className='aa-tab-header'>
        <h5 className='aa-tab-title'>User Activity</h5>
        <RangeSelector
          value={days}
          onChange={setDays}
        />
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorAlert message={error} />}

      {data && !loading && (
        <>
          <div className='aa-stat-row'>
            <StatCard
              label='Total Users'
              value={data.totalUsers.toLocaleString()}
            />
            <StatCard
              label='DAU'
              value={data.dau.toLocaleString()}
              sub='last 24 h'
            />
            <StatCard
              label='WAU'
              value={data.wau.toLocaleString()}
              sub='last 7 days'
            />
            <StatCard
              label='MAU'
              value={data.mau.toLocaleString()}
              sub='last 30 days'
            />
          </div>

          <div className='aa-chart-row'>
            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Active Users / Day</div>
              <ResponsiveContainer
                width='100%'
                height={220}
              >
                <LineChart data={data.activeUsersByDay}>
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip labelFormatter={(v: unknown) => String(v)} />
                  <Line
                    type='monotone'
                    dataKey='count'
                    name='Active Users'
                    stroke={COLORS[0]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className='aa-chart-card'>
              <div className='aa-chart-title'>New Signups / Day</div>
              <ResponsiveContainer
                width='100%'
                height={220}
              >
                <BarChart data={data.newSignupsByDay}>
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip labelFormatter={(v: unknown) => String(v)} />
                  <Bar
                    dataKey='count'
                    name='Signups'
                    fill={COLORS[1]}
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Search Funnel Tab ─────────────────────────────────────────────────────
function SearchTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<SearchFunnelAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await adminAnalyticsService.getSearchFunnel(days));
    } catch {
      setError('Failed to load search analytics.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className='aa-tab-header'>
        <h5 className='aa-tab-title'>Search Funnel</h5>
        <RangeSelector
          value={days}
          onChange={setDays}
        />
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorAlert message={error} />}

      {data && !loading && (
        <>
          <div className='aa-stat-row'>
            <StatCard
              label='Total Searches'
              value={data.totalSearches.toLocaleString()}
            />
            <StatCard
              label='Conversions'
              value={data.totalConversions.toLocaleString()}
              sub='result selected'
            />
            <StatCard
              label='Conversion Rate'
              value={`${data.conversionRate}%`}
            />
          </div>

          <div className='aa-chart-row'>
            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Searches & Conversions / Day</div>
              <ResponsiveContainer
                width='100%'
                height={220}
              >
                <LineChart data={data.byDay}>
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip labelFormatter={(v: unknown) => String(v)} />
                  <Legend />
                  <Line
                    type='monotone'
                    dataKey='count'
                    name='Searches'
                    stroke={COLORS[0]}
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type='monotone'
                    dataKey='conversions'
                    name='Conversions'
                    stroke={COLORS[2]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className='aa-chart-card'>
              <div className='aa-chart-title aa-chart-title--row'>
                By Endpoint
                {data.byEndpoint.length > 0 && (
                  <button
                    className='btn btn-xs btn-outline-secondary aa-export-btn'
                    onClick={() =>
                      exportToCsv(
                        'search-by-endpoint.csv',
                        data.byEndpoint as unknown as Record<
                          string,
                          string | number
                        >[],
                        [
                          { key: 'endpoint', label: 'Endpoint' },
                          { key: 'count', label: 'Searches' },
                          { key: 'conversionRate', label: 'Conv.%' },
                          { key: 'avgResultCount', label: 'Avg Results' }
                        ]
                      )
                    }
                  >
                    Export CSV
                  </button>
                )}
              </div>
              <table className='table table-sm aa-table'>
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th className='text-end'>Searches</th>
                    <th className='text-end'>Conv.%</th>
                    <th className='text-end'>Avg Results</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byEndpoint.map((e) => (
                    <tr key={e.endpoint}>
                      <td className='text-truncate aa-endpoint-cell'>
                        {e.endpoint}
                      </td>
                      <td className='text-end'>{e.count.toLocaleString()}</td>
                      <td className='text-end'>{e.conversionRate}%</td>
                      <td className='text-end'>{e.avgResultCount}</td>
                    </tr>
                  ))}
                  {data.byEndpoint.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className='text-center text-muted'
                      >
                        No data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className='aa-chart-card aa-chart-card--full'>
            <div className='aa-chart-title'>Top Queries</div>
            {data.topQueries.length === 0 ? (
              <p className='text-muted text-center py-2'>
                No queries recorded yet.
              </p>
            ) : (
              <ResponsiveContainer
                width='100%'
                height={200}
              >
                <BarChart
                  data={data.topQueries}
                  layout='vertical'
                  margin={{ left: 120, right: 20 }}
                >
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    type='number'
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    type='category'
                    dataKey='query'
                    tick={{ fontSize: 11 }}
                    width={115}
                  />
                  <Tooltip />
                  <Bar
                    dataKey='count'
                    name='Searches'
                    fill={COLORS[3]}
                    radius={[0, 3, 3, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Content Tab ───────────────────────────────────────────────────────────
function ContentTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ContentAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await adminAnalyticsService.getContent(days));
    } catch {
      setError('Failed to load content analytics.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className='aa-tab-header'>
        <h5 className='aa-tab-title'>Content Activity</h5>
        <RangeSelector
          value={days}
          onChange={setDays}
        />
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorAlert message={error} />}

      {data && !loading && (
        <>
          <div className='aa-stat-row'>
            <StatCard
              label='Trip Creations'
              value={data.tripCreations.toLocaleString()}
              sub={`last ${days} days`}
            />
            <StatCard
              label='Activities Added'
              value={data.activityAdditions.toLocaleString()}
              sub={`last ${days} days`}
            />
          </div>

          <div className='aa-chart-row'>
            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Write Operations / Day</div>
              <ResponsiveContainer
                width='100%'
                height={220}
              >
                <BarChart data={data.byDay}>
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip labelFormatter={(v: unknown) => String(v)} />
                  <Bar
                    dataKey='mutations'
                    name='Write Ops'
                    fill={COLORS[2]}
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Top Write Endpoints</div>
              <table className='table table-sm aa-table'>
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                    <th className='text-end'>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topEndpoints.map((e, i) => (
                    <tr key={i}>
                      <td>
                        <span
                          className={`badge bg-${e.method === 'DELETE' ? 'danger' : e.method === 'PUT' ? 'warning' : 'primary'}`}
                        >
                          {e.method}
                        </span>
                      </td>
                      <td className='text-truncate aa-endpoint-cell'>
                        {e.endpoint}
                      </td>
                      <td className='text-end'>{e.count.toLocaleString()}</td>
                    </tr>
                  ))}
                  {data.topEndpoints.length === 0 && (
                    <tr>
                      <td
                        colSpan={3}
                        className='text-center text-muted'
                      >
                        No data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── AI Usage Tab ──────────────────────────────────────────────────────────
function AiUsageTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AiUsageAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await adminAnalyticsService.getAiUsage(days));
    } catch {
      setError('Failed to load AI usage analytics.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className='aa-tab-header'>
        <h5 className='aa-tab-title'>AI Usage</h5>
        <RangeSelector
          value={days}
          onChange={setDays}
        />
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorAlert message={error} />}

      {data && !loading && (
        <>
          <div className='aa-stat-row'>
            <StatCard
              label='Total Calls'
              value={data.totalCalls.toLocaleString()}
            />
            <StatCard
              label='Total Tokens'
              value={data.totalTokens.toLocaleString()}
            />
            <StatCard
              label='Est. Cost (USD)'
              value={`$${data.totalCostUsd.toFixed(4)}`}
            />
          </div>

          <div className='aa-chart-row'>
            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Tokens / Day</div>
              <ResponsiveContainer
                width='100%'
                height={220}
              >
                <LineChart data={data.byDay}>
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={(v: unknown) => String(v)} />
                  <Line
                    type='monotone'
                    dataKey='totalTokens'
                    name='Tokens'
                    stroke={COLORS[4]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Cost / Day (USD)</div>
              <ResponsiveContainer
                width='100%'
                height={220}
              >
                <BarChart data={data.byDay}>
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: unknown) => `$${v}`}
                  />
                  <Tooltip
                    labelFormatter={(v: unknown) => String(v)}
                    formatter={(v: unknown) => [
                      `$${Number(v ?? 0).toFixed(4)}`,
                      'Cost'
                    ]}
                  />
                  <Bar
                    dataKey='totalCostUsd'
                    name='Cost'
                    fill={COLORS[5]}
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className='aa-chart-row'>
            <div className='aa-chart-card'>
              <div className='aa-chart-title'>By Provider</div>
              {data.byProvider.length > 0 ? (
                <div className='aa-pie-wrapper'>
                  <ResponsiveContainer
                    width='50%'
                    height={180}
                  >
                    <PieChart>
                      <Pie
                        data={data.byProvider}
                        dataKey='totalCostUsd'
                        nameKey='provider'
                        cx='50%'
                        cy='50%'
                        outerRadius={70}
                        label={({ provider }: any) => provider}
                      >
                        {data.byProvider.map((_, i) => (
                          <Cell
                            key={i}
                            fill={COLORS[i % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v: unknown) =>
                          `$${Number(v ?? 0).toFixed(4)}`
                        }
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <table className='table table-sm aa-table aa-pie-table'>
                    <thead>
                      <tr>
                        <th>Provider</th>
                        <th className='text-end'>Calls</th>
                        <th className='text-end'>Tokens</th>
                        <th className='text-end'>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byProvider.map((p) => (
                        <tr key={p.provider}>
                          <td>{p.provider}</td>
                          <td className='text-end'>
                            {p.calls.toLocaleString()}
                          </td>
                          <td className='text-end'>
                            {p.totalTokens.toLocaleString()}
                          </td>
                          <td className='text-end'>
                            ${p.totalCostUsd.toFixed(4)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className='text-muted text-center py-3'>
                  No AI calls recorded yet.
                </p>
              )}
            </div>

            <div className='aa-chart-card'>
              <div className='aa-chart-title aa-chart-title--row'>
                By Feature (top 15)
                {data.byFeature.length > 0 && (
                  <button
                    className='btn btn-xs btn-outline-secondary aa-export-btn'
                    onClick={() =>
                      exportToCsv(
                        'ai-by-feature.csv',
                        data.byFeature.map((f) => ({
                          feature: f.feature,
                          calls: f.calls,
                          totalTokens: f.totalTokens,
                          totalCostUsd: f.totalCostUsd
                        })),
                        [
                          { key: 'feature', label: 'Feature' },
                          { key: 'calls', label: 'Calls' },
                          { key: 'totalTokens', label: 'Tokens' },
                          { key: 'totalCostUsd', label: 'Cost USD' }
                        ]
                      )
                    }
                  >
                    Export CSV
                  </button>
                )}
              </div>
              <table className='table table-sm aa-table'>
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th className='text-end'>Calls</th>
                    <th className='text-end'>Tokens</th>
                    <th className='text-end'>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byFeature.map((f) => (
                    <tr key={f.feature}>
                      <td className='text-truncate aa-endpoint-cell'>
                        {f.feature}
                      </td>
                      <td className='text-end'>{f.calls.toLocaleString()}</td>
                      <td className='text-end'>
                        {f.totalTokens.toLocaleString()}
                      </td>
                      <td className='text-end'>${f.totalCostUsd.toFixed(4)}</td>
                    </tr>
                  ))}
                  {data.byFeature.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className='text-center text-muted'
                      >
                        No data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── API Requests Tab ──────────────────────────────────────────────────────
function ApiRequestsTab() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<ApiRequestsAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await adminAnalyticsService.getApiRequests(days));
    } catch {
      setError('Failed to load API request analytics.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className='aa-tab-header'>
        <h5 className='aa-tab-title'>API Requests</h5>
        <RangeSelector
          value={days}
          onChange={setDays}
        />
      </div>

      {loading && <LoadingSpinner />}
      {error && <ErrorAlert message={error} />}

      {data && !loading && (
        <>
          <div className='aa-stat-row'>
            <StatCard
              label='Total Requests'
              value={data.totalRequests.toLocaleString()}
              sub={`last ${days} days`}
            />
            <StatCard
              label='Avg Latency'
              value={`${data.avgLatencyMs} ms`}
            />
            <StatCard
              label='Error Rate'
              value={`${data.errorRate}%`}
              sub='4xx + 5xx'
            />
          </div>

          <div className='aa-chart-row'>
            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Requests / Day</div>
              <ResponsiveContainer
                width='100%'
                height={220}
              >
                <BarChart data={data.byDay}>
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={(v: unknown) => String(v)} />
                  <Legend />
                  <Bar
                    dataKey='count'
                    name='Requests'
                    fill={COLORS[0]}
                    radius={[3, 3, 0, 0]}
                    stackId='a'
                  />
                  <Bar
                    dataKey='errors'
                    name='Errors'
                    fill={COLORS[4]}
                    radius={[3, 3, 0, 0]}
                    stackId='a'
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Avg Latency / Day (ms)</div>
              <ResponsiveContainer
                width='100%'
                height={220}
              >
                <LineChart data={data.byDay}>
                  <CartesianGrid
                    strokeDasharray='3 3'
                    stroke='#e5e7eb'
                  />
                  <XAxis
                    dataKey='date'
                    tickFormatter={shortDate}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={(v: unknown) => String(v)}
                    formatter={(v: unknown) => [
                      `${Number(v ?? 0)} ms`,
                      'Avg Latency'
                    ]}
                  />
                  <Line
                    type='monotone'
                    dataKey='avgLatencyMs'
                    name='Latency'
                    stroke={COLORS[3]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className='aa-chart-row'>
            <div className='aa-chart-card'>
              <div className='aa-chart-title'>Status Breakdown</div>
              {data.statusBreakdown.length > 0 ? (
                <div className='aa-pie-wrapper'>
                  <ResponsiveContainer
                    width='40%'
                    height={160}
                  >
                    <PieChart>
                      <Pie
                        data={data.statusBreakdown}
                        dataKey='count'
                        nameKey='statusClass'
                        cx='50%'
                        cy='50%'
                        outerRadius={60}
                        label={({ statusClass }: any) => statusClass}
                      >
                        {data.statusBreakdown.map((_, i) => (
                          <Cell
                            key={i}
                            fill={COLORS[i % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className='aa-status-list'>
                    {data.statusBreakdown.map((s, i) => (
                      <div
                        key={s.statusClass}
                        className='aa-status-item'
                      >
                        <span className={`aa-status-dot aa-status-dot--${i}`} />
                        <span className='aa-status-class'>{s.statusClass}</span>
                        <span className='aa-status-count'>
                          {s.count.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className='text-muted text-center py-3'>No data yet.</p>
              )}
            </div>

            <div className='aa-chart-card'>
              <div className='aa-chart-title aa-chart-title--row'>
                Top Endpoints (by volume)
                {data.topEndpoints.length > 0 && (
                  <button
                    className='btn btn-xs btn-outline-secondary aa-export-btn'
                    onClick={() =>
                      exportToCsv(
                        'api-top-endpoints.csv',
                        data.topEndpoints as unknown as Record<
                          string,
                          string | number
                        >[],
                        [
                          { key: 'endpoint', label: 'Endpoint' },
                          { key: 'count', label: 'Requests' },
                          { key: 'avgLatencyMs', label: 'Avg ms' },
                          { key: 'errorRate', label: 'Err%' }
                        ]
                      )
                    }
                  >
                    Export CSV
                  </button>
                )}
              </div>
              <table className='table table-sm aa-table'>
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th className='text-end'>Requests</th>
                    <th className='text-end'>Avg ms</th>
                    <th className='text-end'>Err%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topEndpoints.map((e) => (
                    <tr key={e.endpoint}>
                      <td className='text-truncate aa-endpoint-cell'>
                        {e.endpoint}
                      </td>
                      <td className='text-end'>{e.count.toLocaleString()}</td>
                      <td className='text-end'>{e.avgLatencyMs}</td>
                      <td className='text-end'>
                        <span
                          className={
                            e.errorRate > 5 ? 'text-danger fw-bold' : ''
                          }
                        >
                          {e.errorRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                  {data.topEndpoints.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className='text-center text-muted'
                      >
                        No data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function AdminAnalyticsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('users');

  return (
    <div className='aa-page'>
      <AdminNavBar />
      <div className='aa-page-header'>
        <h4 className='aa-page-title'>Analytics</h4>
      </div>

      <ul className='nav nav-tabs aa-tabs'>
        {TABS.map((t) => (
          <li
            key={t.id}
            className='nav-item'
          >
            <button
              className={`nav-link ${activeTab === t.id ? 'active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      <div className='aa-tab-content'>
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'search' && <SearchTab />}
        {activeTab === 'content' && <ContentTab />}
        {activeTab === 'ai' && <AiUsageTab />}
        {activeTab === 'api' && <ApiRequestsTab />}
        {activeTab === 'transcription' && <TranscriptionTab />}
      </div>
    </div>
  );
}
